import React, { useState } from "react";
import { ArrowDown2 } from "iconsax-react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Checkbox,
} from "@mui/material";

interface ISection {
  label: string;
  value: string;
  description: string;
  section_key: string;
}
interface IGroupedSections {
  parent: ISection;
  children: ISection[];
}
interface Props {
  selectedIds: number[];
  groupedSections: IGroupedSections[];
  toggleParent: (group: IGroupedSections) => void;
  toggleChild: (child: ISection, group: IGroupedSections) => void;
}

const SectionSelector = React.memo(
  ({ groupedSections, selectedIds, toggleParent, toggleChild }: Props) => {
    const [parentFirstClickPassed, setParentFirtClickPassed] = useState<
      Record<number, boolean>
    >({});

    const handleChangeParent = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      parentId: number
    ) => {
      if (!parentFirstClickPassed[parentId]) {
        setParentFirtClickPassed((prev) => ({ ...prev, [parentId]: true }));
        return;
      }
      e.stopPropagation();
    };

    const handleDeleteParentFirstDelete = (parentId: number) => {
      const newItems = { ...parentFirstClickPassed };
      delete newItems[parentId];
      setParentFirtClickPassed(newItems);
    };

    return (
      <Stack
        sx={{
          py: "8px",
          gap: "8px",
          display: "flex",
          overflowY: "auto",
          maxHeight: "300px",
          flexDirection: "column",
        }}
      >
        {groupedSections.map((group) => {
          const parentId = Number(group.parent.value);

          // if this group has no children then render only parent
          if (group.children.length === 0) {
            return (
              <Stack
                key={parentId}
                direction="row"
                alignItems="center"
                sx={{
                  p: "2px 2px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  background: "#fff",
                  border: "1px solid #ececec",
                }}
                onClick={() => toggleParent(group)}
              >
                <Checkbox checked={selectedIds.includes(parentId)} />
                <div
                  style={{
                    flex: 1,
                    padding: "3px",
                    display: "flex",
                    flexWrap: "wrap",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      gap: "4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        pl: "6px",
                        fontWeight: 400,
                        fontSize: "16px",
                      }}
                    >
                      {group.parent.label.split("/")[1]}
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "30px",
                        height: "fit-content",
                        justifyContent: "center",
                        backgroundColor: "#ebebeb",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "600",
                          fontSize: "0.6rem",
                          padding: "2px 10px",
                        }}
                      >
                        {group.parent.section_key}
                      </span>
                    </div>
                  </div>
                  <Typography
                    flex={1}
                    sx={{
                      fontSize: "10px",
                    }}
                  >
                    {group.parent.description}
                  </Typography>
                </div>
              </Stack>
            );
          }

          const childIds = group.children.map((c) => Number(c.value));
          const isSomeSelected = childIds.some((id) =>
            selectedIds.includes(id)
          );
          const isAllSelected = childIds.every((id) =>
            selectedIds.includes(id)
          );
          return (
            <Accordion
              sx={{
                "&:before": {
                  display: "none",
                },
                boxShadow: "none",
                borderRadius: "4px",
                borderBlock: "none",
                margin: "0 !important",
                background: "#ffffff",
                border: "1px solid #ececec",
              }}
              key={parentId}
              expanded={parentFirstClickPassed[parentId] || false}
            >
              <AccordionSummary
                expandIcon={<ArrowDown2 size={"15px"} />}
                onClick={() => {
                  // handle first time accurdion click to just open it
                  if (!parentFirstClickPassed[parentId]) {
                    setParentFirtClickPassed((prev) => ({
                      ...prev,
                      [parentId]: true,
                    }));
                    return;
                  }
                  handleDeleteParentFirstDelete(parentId);
                }}
                sx={{
                  gap: "10px",
                  display: "flex",
                  padding: "2px 2px",
                  alignItems: "center",
                  "&.Mui-expanded": {
                    minHeight: "unset !important",
                  },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    margin: "0 !important",
                  },
                }}
              >
                <Checkbox
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChangeParent(e, parentId);
                  }}
                  checked={isAllSelected}
                  onChange={() => toggleParent(group)}
                  indeterminate={isSomeSelected && !isAllSelected}
                />
                <Typography sx={{ fontSize: "16px" }}>
                  {group.parent.label.split("/")[1]}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  gap: "0px",
                  padding: "0px",
                  display: "flex",
                  flexDirection: "column",
                  background: "#f5f5f5",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                {group.children.map((child) => {
                  const childId = Number(child.value);
                  const caption =
                    child.label.split("/")[child.label.split("/").length - 1] ||
                    "";

                  return (
                    <Stack
                      sx={{
                        p: "2px 6px",
                        display: "flex",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:first-of-type": {
                          borderTop: "none",
                        },
                        background: "#ffffff",
                        borderTop: "1px solid #e5e5e5",
                        "&:last-of-type": { borderRadius: "0 0 4px 4px" },
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                      spacing={1}
                      key={childId}
                      direction="row"
                      alignItems="center"
                      onClick={() => toggleChild(child, group)}
                    >
                      <div
                        style={{
                          flex: 1,
                          padding: "3px",
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            gap: "4px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              pl: "6px",
                              fontSize: "14px",
                              borderRadius: "4px",
                            }}
                          >
                            {caption}
                          </Typography>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "30px",
                              height: "fit-content",
                              justifyContent: "center",
                              backgroundColor: "#ebebeb",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                fontSize: "0.6rem",
                                padding: "2px 10px",
                                lineHeight: "normal",
                              }}
                            >
                              {child.section_key}
                            </span>
                          </div>
                        </div>
                        <Typography
                          sx={{
                            fontSize: "10px",
                          }}
                          flex={1}
                        >
                          {child.description}
                        </Typography>
                      </div>

                      <Checkbox checked={selectedIds.includes(childId)} />
                    </Stack>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    );
  }
);

export default SectionSelector;
